import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { zodResolver } from "@hookform/resolvers/zod/dist/zod"
import ObjectID from "bson-objectid"
import { exhaustive } from "exhaustive"
import { isPlainObject, map } from "lodash"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useClient } from "../../../Client/ClientAndUserProvider"
import { API, ServerError } from "../../../network/API"
import { DenyButton, FinalizeButton } from "../../../Orders/Components/Form/Buttons/Buttons"
import { ModulePopup } from "../../../Orders/Components/ModulePopup/ModulePopup"
import orderConfirmStyle from "../../../Orders/Components/OrderConfirmCustomerInformation/OrderConfirmCustomerInformation.module.css"
import { SbtH1 } from "../../../Orders/Components/Text/SbtH1/SbtH1"
import { SbtH4 } from "../../../Orders/Components/Text/SbtH4/SbtH4"
import { SbtRHFError } from "../../../Orders/Components/Text/SbtInvalid/SbtRHFError"
import {
	GetOrderEmailReceiver,
	GetOrderEmailReceivers,
	NewOrderEmailReceiverFormSchema,
	NewOrderEmailReceiverFormSchemaType,
	SetOrderEmailReceiver,
	SetOrderEmailReceivers,
	SetOrderEmailReceiverSources,
} from "../types"
import style from "./AddEditOrderEmailReceiverModal.module.css"

export type OrderEmailReceiverModalModeAdd = {
	type: "Add"
}

export type OrderEmailReceiverModalModeEdit = {
	type: "Edit"
	receiver: GetOrderEmailReceiver
}

export type OrderReceiverEmailModalMode = OrderEmailReceiverModalModeAdd | OrderEmailReceiverModalModeEdit

export type AddEditOrderReceiverEmailModalProps = {
	mode: OrderReceiverEmailModalMode
	onClose: () => void
	onDone: () => void
}

enum SubmitFailType {
	Generic = "Generic",
	DuplicatedEmail = "DuplicatedEmail",
	CategoryNotFound = "CategoryNotFound",
}

export const AddEditOrderEmailReceiverModal: FC<AddEditOrderReceiverEmailModalProps> = (
	props: AddEditOrderReceiverEmailModalProps,
) => {
	const client = useClient()

	const form = useRef<HTMLFormElement | null>(null)
	const [submitting, setSubmitting] = useState(false)
	const [submitFailed, setSubmitFailed] = useState<SubmitFailType | null>(null)

	const [currentReceivers, setCurrentReceivers] = useState<GetOrderEmailReceiver[] | null>(null)

	const fetchAvailableReceivers = useCallback(() => {
		setCurrentReceivers(null)
		API.getWithRetries<GetOrderEmailReceivers>(`/customer-portal/order-email-receivers-v1/${client.identifier}`, true)
			.then((res) => {
				setCurrentReceivers(res.receivers)
			})
			.catch(() => {
				setCurrentReceivers([])
			})
	}, [client.identifier])

	useEffect(() => {
		fetchAvailableReceivers()
	}, [fetchAvailableReceivers])

	const {
		register,
		handleSubmit,
		watch,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<NewOrderEmailReceiverFormSchemaType>({
		mode: "onTouched",
		resolver: async (data, context, options) => {
			return zodResolver(NewOrderEmailReceiverFormSchema)(data, context, options)
		},
		defaultValues: exhaustive(props.mode, "type", {
			Add: () => {
				return {
					email: "",
					categoriesToReceiveMailsFor: {
						type: "AllCategories",
					},
				}
			},
			Edit: (it) => {
				const cats: SetOrderEmailReceiverSources = exhaustive(it.receiver.categoriesToReceiveMailsFor, "type", {
					AllCategories: () => {
						return {
							type: "AllCategories",
						}
					},
					SpecificCategories: (it) => {
						return {
							type: "SpecificCategories",
							categoryIds: it.categories.map((x) => x.categoryId),
						}
					},
					_: () => {
						return {
							type: "AllCategories",
						}
					},
				})

				return {
					email: it.receiver.email,
					categoriesToReceiveMailsFor: cats,
				}
			},
		}),
	})

	function onSubmit(formData: NewOrderEmailReceiverFormSchemaType) {
		setSubmitting(true)
		setSubmitFailed(null)

		exhaustive(props.mode, "type", {
			Add: () => {
				setReceiversAndAddNew(formData)
			},
			Edit: (it) => {
				setReceiversAndUpdateExisting(it.receiver.id, formData)
			},
		})
	}

	function handleRequestError(err: any) {
		setSubmitting(false)
		if (err instanceof ServerError) {
			if (err?.data && isPlainObject(err?.data) && "message" in err?.data) {
				const message = err.data.message

				if (message === "duplicate_email") {
					setSubmitFailed(SubmitFailType.DuplicatedEmail)
				} else if (message === "invalid_category") {
					setSubmitFailed(SubmitFailType.CategoryNotFound)
				} else {
					setSubmitFailed(SubmitFailType.Generic)
				}
				return
			}
		}
		setSubmitFailed(SubmitFailType.Generic)
	}

	function setReceiversAndAddNew(formData: NewOrderEmailReceiverFormSchemaType) {
		const newReceiver: SetOrderEmailReceiver = {
			id: ObjectID().toHexString(),
			email: formData.email,
			categoriesToReceiveMailsFor: formData.categoriesToReceiveMailsFor,
		}

		const oldReceivers = (currentReceivers || []).map((receiver) => {
			const cats: SetOrderEmailReceiverSources = exhaustive(receiver.categoriesToReceiveMailsFor, "type", {
				AllCategories: () => {
					return {
						type: "AllCategories",
					}
				},
				SpecificCategories: (it) => {
					return {
						type: "SpecificCategories",
						categoryIds: it.categories.map((x) => x.categoryId),
					}
				},
				_: () => {
					return {
						type: "AllCategories",
					}
				},
			})

			const ret: SetOrderEmailReceiver = {
				id: receiver.id,
				email: receiver.email,
				categoriesToReceiveMailsFor: cats,
			}

			return ret
		})

		const req: SetOrderEmailReceivers = {
			receivers: [...oldReceivers, newReceiver],
		}

		API.postWithRetries<GetOrderEmailReceivers, SetOrderEmailReceivers>(
			`/customer-portal/order-email-receivers-v1/${client.identifier}`,
			req,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function setReceiversAndUpdateExisting(receiverId: string, formData: NewOrderEmailReceiverFormSchemaType) {
		const updatedReceiver: SetOrderEmailReceiver = {
			id: receiverId,
			email: formData.email,
			categoriesToReceiveMailsFor: formData.categoriesToReceiveMailsFor,
		}

		const oldReceivers = (currentReceivers || [])
			.map((receiver) => {
				const cats: SetOrderEmailReceiverSources = exhaustive(receiver.categoriesToReceiveMailsFor, "type", {
					AllCategories: () => {
						return {
							type: "AllCategories",
						}
					},
					SpecificCategories: (it) => {
						return {
							type: "SpecificCategories",
							categoryIds: it.categories.map((x) => x.categoryId),
						}
					},
					_: () => {
						return {
							type: "AllCategories",
						}
					},
				})

				const ret: SetOrderEmailReceiver = {
					id: receiver.id,
					email: receiver.email,
					categoriesToReceiveMailsFor: cats,
				}

				return ret
			})
			.filter((x) => x.id !== receiverId)

		const req: SetOrderEmailReceivers = {
			receivers: [...oldReceivers, updatedReceiver],
		}

		API.postWithRetries<GetOrderEmailReceivers, SetOrderEmailReceivers>(
			`/customer-portal/order-email-receivers-v1/${client.identifier}`,
			req,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function deleteReceiver(receiverId: string) {
		setSubmitting(true)
		setSubmitFailed(null)
		API.deleteWithRetries<GetOrderEmailReceivers>(
			`/customer-portal/order-email-receivers-v1/${client.identifier}/${receiverId}/delete`,
			undefined,
			undefined,
			10,
		).then(
			() => {
				props.onDone()
			},
			(err) => {
				handleRequestError(err)
			},
		)
	}

	function content() {
		return exhaustive(props.mode, "type", {
			Add: () => {
				return addEditReceiverForm()
			},
			Edit: () => {
				return addEditReceiverForm()
			},
		})
	}

	function addEditReceiverForm() {
		return (
			<form style={{ marginTop: "20px" }} onSubmit={handleSubmit(onSubmit)} ref={form} id="addEditReceiverForm">
				<div className={orderConfirmStyle.fields}>
					<label>
						<SbtH4>E-post</SbtH4>
						<input
							disabled={submitting}
							placeholder="exempel@exempel.se"
							className={orderConfirmStyle.input}
							{...register("email")}
						/>
						<SbtRHFError error={errors.email} />
					</label>
					<label>
						<SbtH4>Kategorier att motta e-post för</SbtH4>
						<select
							style={{ cursor: "pointer" }}
							className={orderConfirmStyle.input}
							disabled={submitting}
							{...register("categoriesToReceiveMailsFor.type", {
								onChange: () => {
									setValue("categoriesToReceiveMailsFor.categoryIds", [])
								},
							})}>
							<option value={"AllCategories"}>Alla kategorier</option>
							<option value={"SpecificCategories"}>Specifika kategorier</option>
						</select>
					</label>
					{watch("categoriesToReceiveMailsFor.type") === "SpecificCategories"
						? specificCategoriesSelectionFormElement()
						: null}
				</div>
			</form>
		)
	}

	function specificCategoriesSelectionFormElement() {
		if (getValues("categoriesToReceiveMailsFor.type") !== "SpecificCategories") {
			return null
		}

		return (
			<div className={style.receiversWrapper}>
				{map(client.categories, (category) => {
					return (
						<label key={category.id} className={style.receiver}>
							{category.name}
							<input
								{...register("categoriesToReceiveMailsFor.categoryIds")}
								value={category.id}
								type={"checkbox"}
							/>
						</label>
					)
				})}
			</div>
		)
	}

	const mode = props.mode

	return (
		<ModulePopup
			disableClose={submitting}
			className={style.modal}
			onClose={() => {
				props.onClose()
			}}>
			<div className={style.wrapper}>
				{mode.type === "Add" ? <SbtH1>Lägg till mottagare</SbtH1> : null}
				{mode.type === "Edit" ? <SbtH1>Redigera mottagare</SbtH1> : null}
				<div className={style.content}>{content()}</div>
				<div className={style.buttons}>
					<FinalizeButton
						disabled={submitting}
						onClick={() => {
							form.current?.requestSubmit()
						}}>
						{mode.type === "Add" ? "Lägg till" : null}
						{mode.type === "Edit" ? "Spara" : null}
						{submitting ? (
							<FontAwesomeIcon style={{ color: "gray", marginLeft: "15px" }} spin={true} icon={faSpinner} />
						) : null}
					</FinalizeButton>
					{mode.type === "Edit" ? (
						<DenyButton
							disabled={submitting}
							onClick={() => {
								deleteReceiver(mode.receiver.id)
							}}>
							Ta bort
							{submitting ? (
								<FontAwesomeIcon
									style={{ color: "gray", marginLeft: "15px" }}
									spin={true}
									icon={faSpinner}
								/>
							) : null}
						</DenyButton>
					) : null}
				</div>
				{submitFailed ? (
					<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
						{exhaustive(submitFailed, {
							Generic: () => "Något gick fel, vänligen försök igen",
							DuplicatedEmail: () =>
								"E-posten för mottagaren är en dublett av en annan mottagare, vänligen försök igen",
							CategoryNotFound: () =>
								"En eller flera valda kategorier kunde inte hittas, vänligen ladda om sidan och försök igen",
						})}
					</div>
				) : null}
			</div>
		</ModulePopup>
	)
}
